import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Scrollbars } from "rc-scrollbars";
import ListComponent from "./ListComponent";
import PublicResultsFooter from "./PublicResultsFooter";
import FleetRaceDetails from "./FleetRaceDetailsv2";
import NoResultsPublished from "./NoResultsPublished";

import Logo1 from "../../../assets/images/Logo1.png";
import Logo2 from "../../../assets/images/Logo2.png";
import Logo3 from "../../../assets/images/Logo3.png";
import Logo4 from "../../../assets/images/Logo4.png";
import {
  BACKEND_BASE_URL,
  EVENT_BANNER_PATH,
  EVENT_lOGO_PATH,
  SERIES_BANNER_PATH,
  SERIES_lOGO_PATH,
} from "../../global/constants";
import PublishResultsNoImage from "../../../components/PublishResultsNoImage";
import CompanyLogo from "../../../assets/images/topyacht.svg";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SeriesIndexv2 = ({ isLoggedOutMode }) => {
  const [selectedFleet, setSelectedFleet] = useState(0);

  const [fleets, setFleets] = useState([]);
  const [clubName, setClubName] = useState("");
  const [seriesName, setSeriesName] = useState("");
  const [banner, setBanner] = useState("");
  const [logos, setLogos] = useState([]);
  const [raceSessions, setRaceSessions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [clubId, setClubId] = useState("");
  const [eventId, setEventId] = useState("");

  const queryParameters = new URLSearchParams(window.location.search);
  const fleetIdQ = queryParameters.get("fleetId");
  const fleetNameQ = queryParameters.get("fleetName");
  const raceSessionIdQ = queryParameters.get("raceSessionId");
  const queryParams = [fleetIdQ, raceSessionIdQ, fleetNameQ];
  const paramExist = queryParams.every(
    (value) => value !== null && value !== undefined
  );

  // Resolve seriesId once, up front. A missing param used to be interpolated into
  // the request verbatim ("?seriesId=null"), which fails server-side. The string
  // forms matter too: a link built as `?seriesId=${undefined}` arrives as the
  // literal text "undefined", which is not a missing param as far as
  // URLSearchParams is concerned.
  const rawSeriesId = queryParameters.get("seriesId");
  const seriesIdQ =
    rawSeriesId &&
      rawSeriesId.trim() !== "" &&
      rawSeriesId !== "null" &&
      rawSeriesId !== "undefined"
      ? rawSeriesId.trim()
      : null;

  // A direct hit on this URL (pasted link, bookmark, new tab) has no
  // document.referrer, so there's nowhere meaningful to go "back" to. Coming
  // here via a real click-through from the club's IndexResults listing is a
  // full page navigation (see ListComponent's window.location.href), so the
  // browser does populate document.referrer with that page's URL — use that
  // to decide whether the back button makes sense here.
  const cameFromIndexResults = document.referrer.includes(
    "/publicresults/IndexResults"
  );

  const fetchPublishResults = async () => {
    // Without a series there is nothing to request — bail out with a message the
    // UI actually renders, rather than firing a request that is certain to fail.
    if (!seriesIdQ) {
      setError("No series selected. This page needs a seriesId in the address.");
      setLoading(false);
      return;
    }

    try {
      const encryptedSeriesId = seriesIdQ; //encodeURIComponent(encrypt(JSON.stringify(seriesId)));

      // Construct the full URL
      const url =
        `${BACKEND_BASE_URL}api/PublishResults/clubs/raceSessionsAndFleetsBySeriesId?seriesId=` +
        encodeURIComponent(encryptedSeriesId);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const parseResult = await response.json();
      var result = {
        data: parseResult?.data?.distinctFleets ?? []
      };

      setRaceSessions(parseResult?.data?.raceSessions ?? []);
      setFleets(result.data);
      if (
        result.data != undefined &&
        result.data != null &&
        result.data.length > 0
      ) {
        applySeriesMetadata(result.data[0]);
      } else {
        // The raceSessionsAndFleetsBySeriesId endpoint returns an empty
        // distinctFleets list for a series with no published fleets, and — unlike
        // the clubs/fleets endpoint — carries no series/club metadata in that
        // case. Fall back to clubs/fleets, which synthesises a default row
        // (seriesName, clubName, clubId, eventId, banner, logos) for empty
        // series, so the header still shows and the Back button has a valid
        // clubId/eventId to navigate with instead of crashing on empty params.
        await fetchSeriesMetadataFallback(encryptedSeriesId);
      }
    } catch (err) {
      // Keep the technical detail in the console; show something a visitor to a
      // public results page can act on.
      console.error("Failed to load series results:", err);
      setError("We couldn't load this series. Please check the link and try again.");
    } finally {
      setLoading(false); // Mark loading as complete
    }
  };

  // Populate the series/club header + banner/logos state from a fleet metadata row.
  const applySeriesMetadata = (row) => {
    if (!row) return;
    setEventId(row.eventId);
    setClubId(row.clubId);
    setClubName(row.clubName);
    setSeriesName(row.seriesName);
    setBanner(
      row.banner
        ? SERIES_BANNER_PATH + row.banner
        : row.eventBanner
          ? EVENT_BANNER_PATH + row.eventBanner
          : ""
    );
    setLogos(
      row.logos
        ? row.logos.split(",").map((x) => SERIES_lOGO_PATH + x)
        : row.eventLogos
          ? row.eventLogos.split(",").map((x) => EVENT_lOGO_PATH + x)
          : []
    );
  };

  // Empty-series fallback: fetch metadata from the clubs/fleets endpoint, which
  // returns a default row even when the series has no fleets.
  const fetchSeriesMetadataFallback = async (seriesId) => {
    if (!seriesId) return;
    try {
      const url =
        `${BACKEND_BASE_URL}api/PublishResults/clubs/fleets?seriesId=` +
        encodeURIComponent(seriesId);
      const response = await fetch(url);
      if (!response.ok) return;
      const result = await response.json();
      applySeriesMetadata(result?.data?.[0]);
    } catch (err) {
      // Non-fatal: if this also fails, the header simply stays blank.
    }
  };

  useEffect(() => {
    fetchPublishResults(); // Fetch data when component mounts
  }, []);

  const handleBackToIndex = () => {
    // navigate(-1);
    const origin = window.location.origin; // "http://localhost:3000"
    const url = `${origin}/publicresults/IndexResults?clubId=${clubId}&eventId=${eventId}`;
    window.location.href = url;
  };

  /*  const fleetItems = [
      { label: 'Div 1', link: '' },
      { label: 'Div 2', link: '' },
      { label: 'Div 3', link: '' },
    ];*/

  const fleetLogos = [Logo1, Logo2, Logo3, Logo4];
  // const footerBannerImage = [ClubImage];

  const handleFleetClick = (fleet) => {
    console.log("the fleet 123", fleet);
    setSelectedFleet(fleet);
  };

  useEffect(() => {
    const isLoggedIn = Boolean(localStorage?.getItem("accessToken"));
    const isPublicResultsPage =
      window.location.pathname.startsWith("/publicresults");

    if (!isLoggedIn || !isPublicResultsPage) return;

    const mainElement = document.querySelector(".ty_application_design main");
    if (!mainElement) return;

    // Check if screen is tablet/mobile (md and below) or desktop (lg and above)
    const isTabletOrMobile = window.innerWidth < 1050; // lg breakpoint in MUI is 1200px

    mainElement.style.setProperty(
      "height",
      isTabletOrMobile ? "calc(100% - 50px)" : "calc(100% - 10px)",
      "important"
    );

    return () => {
      mainElement.style.setProperty(
        "height",
        "calc(100% - 70px)",
        "important"
      );
    };
  }, []);

  if (fleetIdQ) {
    return (
      <FleetRaceDetails
        fleetIdQ={fleetIdQ}
        fleetName={fleetNameQ}
        onBack={handleBackToIndex}
        fleetList={fleets}
        raceSessionIdQ={raceSessionIdQ}
        paramExist={paramExist}
        raceSessions={raceSessions}
        isLoggedOutMode={isLoggedOutMode}
      />
    );
  } else if (selectedFleet) {
    return (
      <FleetRaceDetails
        fleet={selectedFleet}
        onBack={handleBackToIndex}
        fleetList={fleets}
        raceSessions={raceSessions}
        isLoggedOutMode={isLoggedOutMode}
      />
    );
  }

  // Matches SeriesIndex.js: a series with no fleets renders the "no results"
  // message instead of an empty fleet picker (and suppresses the banner placeholder).
  const hasFleets = fleets?.length > 0 && fleets?.[0]?.fleet !== null;

  return (
    <div className="ty_public_results_main ty_application_design">
      <Box className="inner_wrapper_pages">
        <Box className="InnerPage_area">
          <Scrollbars
            autoHide
            // autoHeight
            // autoHeightMin={"calc(100vh - 180px)"}
            style={{
              width: "100%",
              height: '100%',
            }}
          >
            {loading ? (
              <CircularProgress sx={{ color: "#fff" }} size={14} />
            ) : error ? (
              // `error` was previously set but never rendered, so a failed load
              // (a missing/unknown seriesId being the common case) left the page
              // showing an empty shell with no explanation.
              <Box className="ty_public_results">
                <Grid container className="ty_pr_main_container">
                  <Grid
                    item
                    lg={12}
                    md={12}
                    xs={12}
                    sx={{ justifyItems: "center", paddingLeft: '0.75rem', paddingRight: '0.75rem' }}
                  >
                    <NoResultsPublished />
                    <Typography
                      className="ty_pr_heading_text"
                      sx={{ textAlign: "center", marginTop: "8px" }}
                    >
                      {error}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box className="ty_public_results">
                <Grid container className="ty_pr_main_container">
                  <Grid
                    item
                    lg={12}
                    md={12}
                    xs={12}
                    sx={{ justifyItems: "center", width: "100%", padding: '0', ...(isLoggedOutMode ? { marginTop: "0.5rem" } : {}) }}
                  >
                    {banner ? (
                      <img
                        src={banner}
                        alt="club image"
                        className="ty_pr_club_image"
                      />
                    ) : (
                      <div className="BannerHandler">
                        <PublishResultsNoImage
                          tagName={"Banner"}
                          boxWidth={"20%"}
                          boxHeight={"100px"}
                        />
                      </div>
                    )}
                  </Grid>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    xs={12}
                    sx={{ justifyItems: "center", padding: '0 0' }}
                  >
                    <Box className="ty_pr_series_header">
                      {cameFromIndexResults && (
                        <Box className="ty_pr_BackBtn">
                          <Button
                            className="ty_pr_back_button"
                            variant="contained"
                            onClick={handleBackToIndex}
                          >
                            <span className="ty_back_icon" style={{ backgroundColor: "#356dad" }}>
                              <ArrowBackIcon sx={{ fontSize: { xs: "16px !important", sm: "18px !important" } }} />
                            </span>
                          </Button>
                        </Box>
                      )}
                      <div style={{ textAlign: 'center', marginTop: "0.25rem" }}>
                        <Typography className="ty_pr_series_caption_text" mb={'6px'}>
                          {seriesName}
                        </Typography>
                        <Typography className="ty_pr_heading_text">
                          Hosted by <>{clubName}</>
                        </Typography>
                      </div>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    xs={12}
                    pt={1}
                    sx={{ justifyItems: "center", paddingTop: '0', paddingLeft: '0.75rem', paddingRight: '0.75rem' }}
                  >
                    {hasFleets ? (
                      <Grid container className="ty_pr_container">
                        <Grid item xs={12} lg={6.5} md={6.5} justifyItems='center'>
                          <Box className="ty_pr_Box">
                            <Box className="ty_pr_BoxBody">
                              <Box className="ty_pr_Box_header">
                                <p
                                  variant="h6"
                                >
                                  Choose your fleet
                                </p>
                              </Box>
                            </Box>
                            <ListComponent
                              listItems={fleets}
                              onItemClick={handleFleetClick}
                            />
                          </Box>

                        </Grid>
                      </Grid>
                    ) : (
                      <NoResultsPublished />
                    )}
                  </Grid>
                </Grid>
                <Grid container>
                  {logos?.length > 0 ?
                    <Grid item xs={12}>
                      <PublicResultsFooter
                        footerLogos={logos}
                        footerBannerImage={banner}
                      />
                    </Grid>
                    : ""}
                  <Box
                    sx={{
                      px: 1,
                      py: 1,
                      width: '100%',
                      // The shared .result-by rule adds a 4px top margin that
                      // pads the bar out without aligning anything.
                      "& .result-by": { marginTop: 0 },
                    }}
                    className="ty_pr_StikcyFooter"
                  >
                    <Typography className="result-by">Results By </Typography>
                    <img
                      src={CompanyLogo}
                      alt="Banner"
                      style={{
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Grid>
              </Box>
            )}
          </Scrollbars>
        </Box>
      </Box>
    </div>
  );
};

export default SeriesIndexv2;